/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 * 
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import LoadingScreen from "components/LoadingScreen";
import useTryCatch from "hooks/useTryCatch";
import PageNoAccess from "pages/FallbackPages/PageNoAccess";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAuth } from "state/AuthContext";
import { useGlobal } from "state/GeneralContext";
import { useInvitation } from "state/InvitationContext";
import { useUser } from "state/UserContext";
import warning from "utils/warning";



const DynamicRouteManager = ({ children }) => {
  const { userId } = useParams();
  const { pathname } = useLocation();
  const { isLoggedIn } = useAuth();
  const { isFormCompleted } = useUser();
  const { validRoutes, setValidRoutes, isFirstRender, setIsFirstRender } =
    useGlobal();
  const [isValid, setIsValid] = useState(true);
  const { attendees, isLoadingAttendees, hasFetchedAttendees } = useInvitation({
    fetchAttendees: isLoggedIn,
    hardFetch: true,
  });
  const [isLoading, setIsLoading] = useState(isFirstRender || isLoadingAttendees);
  const fetchToValidateRoute = useTryCatch(async () => {
    setIsLoading(true);
    if (isLoadingAttendees || !hasFetchedAttendees) return
    const target = attendees.find(
      (c) => c.id == userId || c.hash_id === userId
    );

    if (!target) {
      setIsValid(false);
    } else {
      setValidRoutes(pathname);
      setIsValid(true);
    }
    setIsFirstRender(false);
    setIsLoading(false);
  }, {
    setLoading: setIsLoading,
    onError: (error) => {
      warning(error)
    }
  });
  useEffect(() => {
    const hasNewCompanionsToCreate = attendees.some((c) => !c || !c.hasOwnProperty("id"));
    if (userId === "new" && hasNewCompanionsToCreate) return setIsLoading(false);
    if (validRoutes.includes(pathname)) return setIsLoading(false);
    if (!isFirstRender && isLoading) return setIsLoading(false);
    if (isLoggedIn && !isLoadingAttendees && isFirstRender) {
      fetchToValidateRoute();
    }
  }, [isLoadingAttendees, isLoggedIn]);

  if (isLoading) {
    return <LoadingScreen className="h-48 w-48" />;
  }
  // I know this is so messy, but I only want to validate if the user is trying to enter through url and not navigation.
  if (!isValid) {
    return <PageNoAccess />;
  }
  return children;
};

export default DynamicRouteManager;